import {
  AspectRatio,
  Box,
  BoxProps,
  HStack,
  Image,
  Text,
} from "@chakra-ui/react";
import HoneyAmount from "components/HoneyAmount";
import TraitImage from "components/TraitImage";
import { defaultBearColor, publicUrl } from "config";
import { BigNumber } from "ethers";
import { ImageSize, TraitCategory } from "types";
import { capitalize } from "utils/stringUtils";
import { getTraitCategory } from "utils/traitsUtils";

type TraitTileProps = Omit<BoxProps, "color" | "onChange"> & {
  tokenId: number;
  color?: number;
  name?: string;
  price?: BigNumber;
  canvasSize?: BoxProps["w"];
  imageSize?: ImageSize;
  isDisabled?: boolean;
  onClick?: (tokenId: number) => void;
};

const TraitTile: React.FC<TraitTileProps> = ({
  tokenId,
  price,
  name,
  color = defaultBearColor,
  canvasSize = "14",
  imageSize = "256",
  children,
  isDisabled = false,
  onClick,
  ...rest
}) => {
  const category = getTraitCategory(tokenId);

  return (
    <Box
      bg="dark.500"
      p="3"
      w="full"
      userSelect="none"
      cursor={isDisabled ? "not-allowed" : onClick ? "pointer" : "default"}
      transition="all 0.15s"
      color="bright.500"
      tabIndex={onClick ? 0 : undefined}
      onClick={() => {
        if (!isDisabled && onClick) {
          onClick(tokenId);
        }
      }}
      {...rest}
    >
      <HStack spacing="3" w="full" align="center">
        <AspectRatio
          ratio={1}
          bg="dark.400"
          w={canvasSize}
          h={canvasSize}
          flex="none"
        >
          <>
            <Image
              src={`${publicUrl}/bear-placeholder_transparent.png`}
              w="full"
              transition="opacity 0.15s"
              position="absolute"
              zIndex={category === TraitCategory.Background ? 10 : 0}
            />
            <TraitImage
              tokenId={tokenId}
              color={color}
              size={imageSize}
              position="absolute"
              zIndex={5}
            />
          </>
        </AspectRatio>

        <Box w="full" minW="0" whiteSpace="nowrap">
          <Text fontWeight="semibold" overflow="hidden" textOverflow="ellipsis">
            {name} #{tokenId}
          </Text>
          <Text
            fontSize="xs"
            opacity={0.5}
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {capitalize(category)}
          </Text>
        </Box>

        {price && (
          <HoneyAmount
            amount={price}
            color="secondary.500"
            fontWeight="semibold"
            gap="1.5"
          />
        )}

        {children && <Box flex="none">{children}</Box>}
      </HStack>
    </Box>
  );
};

export default TraitTile;
